import type { JSX } from 'react';
import { motion } from 'framer-motion';
import type { DuckColor, DuckSpecies } from '@duck/contracts';
import { revealUp, revealItem, staggerParent, inViewOnce, useRevealGate } from './anim';
import { GalleryCritter } from './GalleryCritter';
import { SectionGlow } from './SectionGlow';

/** A curated cast for the wall — a spread of species + colours, a couple emoting for life. */
const CAST: { species: DuckSpecies; color: DuckColor; activity: string; name: string }[] = [
  { species: 'duck', color: 'yellow', activity: 'wave', name: 'Duck' },
  { species: 'fox', color: 'brown', activity: 'idle', name: 'Fox' },
  { species: 'frog', color: 'green', activity: 'vibe', name: 'Frog' },
  { species: 'cat', color: 'white', activity: 'idle', name: 'Cat' },
  { species: 'penguin', color: 'blue', activity: 'idle', name: 'Penguin' },
  { species: 'axolotl', color: 'pink', activity: 'dance', name: 'Axolotl' },
  { species: 'dino', color: 'green', activity: 'idle', name: 'Dino' },
  { species: 'capybara', color: 'brown', activity: 'idle', name: 'Capybara' },
];

/**
 * Gallery — a rich, "expensive"-feeling wall of the actual cast. Each card renders a LIVE procedural
 * {@link GalleryCritter} (real avatar, idling), intersection-observed so only on-screen cards spin Pixi.
 * Reduced-motion stills each critter; no-WebGL falls back to the species emoji. Cards have a soft hover lift.
 */
export function GallerySection(): JSX.Element {
  const reduce = useRevealGate();
  return (
    <section id="gallery" aria-labelledby="gallery-heading" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-28 lg:px-8">
      <SectionGlow tone="cool" side="right" />
      <motion.div
        variants={reduce ? undefined : revealUp}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="mx-auto max-w-2xl text-center"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-300/80">Meet the world</p>
        <h2 id="gallery-heading" className="mt-4 text-balance text-3xl font-bold tracking-tight text-white sm:text-5xl">
          Pick your character. Make it yours.
        </h2>
        <p className="mt-4 text-lg text-white/60">
          Twenty critters on one hand-drawn rig, recolourable, with hats, props and emotes. This is the
          real art — alive, right here.
        </p>
      </motion.div>

      <motion.ul
        variants={reduce ? undefined : staggerParent}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4"
      >
        {CAST.map((c) => (
          <motion.li
            key={`${c.species}-${c.color}`}
            variants={reduce ? undefined : revealItem}
            className="group overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.01] p-3 transition duration-300 hover:-translate-y-1 hover:border-amber-300/30 hover:shadow-[0_20px_50px_-20px_rgba(255,210,63,0.25)]"
          >
            {/* Reserved square stage (aspect-square) → no layout shift as Pixi mounts. */}
            <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-[radial-gradient(120%_120%_at_50%_20%,rgba(255,210,63,0.10),transparent_60%)] ring-1 ring-white/5 transition group-hover:bg-[radial-gradient(120%_120%_at_50%_20%,rgba(255,210,63,0.18),transparent_60%)]">
              <GalleryCritter species={c.species} color={c.color} activity={c.activity} />
            </div>
            <p className="mt-2 text-center text-sm font-semibold text-white/75">{c.name}</p>
          </motion.li>
        ))}
      </motion.ul>
    </section>
  );
}
